import { useMemo, useState } from 'react';
import { Navigate, useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import {
  mapPromotionalAdError,
  useAuthSession,
  useStorePromotionalAd,
  useTranslation,
  useUpdatePromotionalAd,
  type PromotionalAdErrorKey,
  type StorePromotionalAdStoreContext,
  type TranslationKey,
} from '@dukanti/shared';
import { AlertCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { supabase } from '@/lib/supabase';
import { useCurrentStore } from '@/hooks/use-current-store';
import { SkeletonBar } from '@/features/products/components/skeleton-bar';
import {
  PromotionalAdFormPage,
  type PromotionalAdFormSubmit,
} from '@/features/promotional-ads/components/promotional-ad-form-page';
import { usePromotionalAdsAccess } from '@/features/promotional-ads/hooks/use-promotional-ads-access';

function toStoreContext(
  storeId: string | null,
  storeSlug: string | null,
): StorePromotionalAdStoreContext | null {
  if (!storeId || !storeSlug) return null;
  return { storeId, storeSlug };
}

export function PromotionalAdEditRoute() {
  const { t, dir } = useTranslation();
  const navigate = useNavigate();
  const { adId } = useParams<{ adId: string }>();
  const authSession = useAuthSession(supabase);
  const { storeId, storeSlug } = useCurrentStore(authSession);
  const storeContext = useMemo(
    () => toStoreContext(storeId, storeSlug ?? null),
    [storeId, storeSlug],
  );
  // Epic #228 P7: editing an ad is legacy-only authoring.
  const access = usePromotionalAdsAccess();
  const isAuthoring = access.state === 'authoring';
  const adQuery = useStorePromotionalAd(supabase, {
    store: isAuthoring ? storeContext : null,
    adId: adId ?? null,
  });
  const updateAd = useUpdatePromotionalAd(supabase);
  const [submitError, setSubmitError] = useState<PromotionalAdErrorKey | null>(null);

  async function handleSubmit(values: PromotionalAdFormSubmit): Promise<void> {
    setSubmitError(null);
    if (!storeContext || !adQuery.data) {
      setSubmitError('admin.promotionalAds.errors.permissionDenied');
      return;
    }

    try {
      await updateAd.mutateAsync({
        ad_id: adQuery.data.id,
        store_id: storeContext.storeId,
        store_slug: storeContext.storeSlug,
        title: values.title,
        alt_text: values.alt_text,
        destination: values.destinationChanged ? values.destination : undefined,
        is_active: values.is_active,
        image: values.image,
      });
      toast.success(t('admin.promotionalAds.toasts.updated' as TranslationKey));
      navigate('/promotions', { replace: true });
    } catch (err) {
      const mapped = mapPromotionalAdError(err);
      setSubmitError(mapped);
      toast.error(t(mapped as TranslationKey));
    }
  }

  if (access.state !== 'authoring' && access.state !== 'loading') {
    return <Navigate to="/promotions" replace />;
  }

  return (
    <main dir={dir} data-testid="promotional-ad-edit-route" className="bg-background min-h-screen">
      {access.state === 'loading' || adQuery.isLoading ? (
        <PromotionalAdEditSkeleton loadingLabel={t('common.loading')} />
      ) : adQuery.isError || !storeContext ? (
        <PromotionalAdInlineError
          title={t('common.error')}
          description={t('common.errors.unknown' as TranslationKey)}
          retryLabel={t('common.retry')}
          onRetry={() => void adQuery.refetch()}
        />
      ) : adQuery.data === null ? (
        <PromotionalAdInlineError
          title={t('admin.promotionalAds.errors.notFound' as TranslationKey)}
          description={t('admin.promotionalAds.errors.notFound' as TranslationKey)}
          retryLabel={t('admin.promotionalAds.form.back' as TranslationKey)}
          onRetry={() => navigate('/promotions')}
        />
      ) : (
        <PromotionalAdFormPage
          mode="edit"
          storeId={storeContext.storeId}
          storeSlug={storeContext.storeSlug}
          ad={adQuery.data}
          isSubmitting={updateAd.isPending}
          errorKey={submitError}
          onSubmit={handleSubmit}
          onCancel={() => navigate('/promotions')}
        />
      )}
    </main>
  );
}

interface PromotionalAdInlineErrorProps {
  readonly title: string;
  readonly description: string;
  readonly retryLabel: string;
  readonly onRetry: () => void;
}

function PromotionalAdInlineError({
  title,
  description,
  retryLabel,
  onRetry,
}: PromotionalAdInlineErrorProps) {
  return (
    <div className="mx-auto max-w-lg px-6 py-16 text-center" role="alert">
      <div className="border-border bg-card shadow-admin-card rounded-lg border p-8">
        <AlertCircle className="text-destructive mx-auto mb-4 size-10" strokeWidth={1.5} />
        <h1 className="font-display text-foreground mb-2 text-xl">{title}</h1>
        <p className="text-muted-foreground mb-5 text-sm">{description}</p>
        <Button type="button" variant="outline" onClick={onRetry}>
          {retryLabel}
        </Button>
      </div>
    </div>
  );
}

function PromotionalAdEditSkeleton({ loadingLabel }: { readonly loadingLabel: string }) {
  return (
    <div
      className="mx-auto max-w-screen-md px-6 pb-32 pt-8"
      role="status"
      aria-busy="true"
      aria-live="polite"
      data-testid="promotional-ad-edit-skeleton"
    >
      <span className="sr-only">{loadingLabel}</span>
      <SkeletonBar className="mb-6 h-4 w-24" />
      <SkeletonBar className="mb-8 h-9 w-64" />
      <div className="border-border bg-card shadow-admin-card rounded-lg border p-6 sm:p-8">
        <SkeletonBar className="mb-6 h-32 w-full rounded-lg" />
        <SkeletonBar className="mb-2 h-4 w-20" />
        <SkeletonBar className="mb-5 h-10 w-full rounded-md" />
        <SkeletonBar className="mb-2 h-4 w-24" />
        <SkeletonBar className="mb-5 h-24 w-full rounded-md" />
        <SkeletonBar className="h-24 w-full rounded-md" />
      </div>
    </div>
  );
}
